import React, { useState } from 'react';
import { Check, Plus, ChevronDown, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { cn } from '../../lib/utils';

const PickerSheet = ({
  title,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  emptyLabel = 'Not set',
  allowAddNew = true,
  validateAddNew,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [error, setError] = useState('');

  const handleSelect = (option) => {
    onChange(option);
    setOpen(false);
    setIsAddingNew(false);
    setNewValue('');
    setError('');
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewValue('');
    setError('');
  };

  const confirmAddNew = () => {
    const trimmedValue = newValue.trim();

    if (!trimmedValue) {
      setError('Value cannot be empty');
      return;
    }

    if (validateAddNew && !validateAddNew(trimmedValue)) {
      return;
    }

    handleSelect(trimmedValue);
  };

  const cancelAddNew = () => {
    setIsAddingNew(false);
    setNewValue('');
    setError('');
  };

  const displayValue = value || emptyLabel;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex h-11 w-full items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className
        )}
      >
        <span className={cn(
          'truncate',
          !value && 'text-muted-foreground'
        )}>
          {displayValue}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>

      <Dialog open={open} onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen) {
          setIsAddingNew(false);
          setNewValue('');
          setError('');
        }
      }}>
        <DialogContent
          variant="sheet"
          className="m-0 p-0"
        >
          <DialogHeader className="p-4 pb-2 border-b sticky top-0 bg-background rounded-t-2xl flex flex-row items-center justify-between">
            <DialogTitle>{title}</DialogTitle>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full p-2 hover:bg-secondary transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {!isAddingNew ? (
              <>
                {options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className="flex items-center justify-between w-full px-4 py-4 text-sm hover:bg-secondary transition-colors min-h-[44px]"
                  >
                    <span className="truncate">{option}</span>
                    {value === option && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </button>
                ))}

                {allowAddNew && (
                  <button
                    type="button"
                    onClick={handleAddNew}
                    className="flex items-center w-full px-4 py-4 text-sm text-primary hover:bg-secondary transition-colors border-t border-border/50 mt-2 min-h-[44px]"
                  >
                    <Plus className="h-4 w-4 mr-2 shrink-0" />
                    Add New...
                  </button>
                )}
              </>
            ) : (
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="new-value" className="text-xs">
                        Enter new {title.toLowerCase()}
                      </Label>
                      <Input
                        id="new-value"
                        value={newValue}
                        onChange={(e) => {
                          setNewValue(e.target.value);
                          if (error) setError('');
                        }}
                        placeholder={placeholder}
                        className="h-11 text-sm"
                        autoFocus
                      />
                      {error && (
                        <p className="text-xs text-destructive">{error}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="border-t border-border p-4 bg-background">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={cancelAddNew}
                      className="flex-1 h-11 text-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={confirmAddNew}
                      className="flex-1 h-11 text-sm"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PickerSheet;
